import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import { getAccessToken } from '../shared/api/tokenStore'
import { notificationsAPI } from '../api/notifications'
import { parseUTC } from '../utils/dateUtils'
import { usePracticeReset } from '../shared/tour/usePracticeReset'
import { APP_STORAGE_PREFIX } from '../config/config.js';

const NotificationCenterContext = createContext()

// eslint-disable-next-line react-refresh/only-export-components
export const useNotificationCenter = () => {
  const context = useContext(NotificationCenterContext)
  if (!context) {
    throw new Error('useNotificationCenter must be used within a NotificationCenterProvider')
  }
  return context
}

// Notification priority mapping
const PRIORITY_MAP = {
  flagged: 'high',
  rejected: 'high',
  conflict: 'high',
  high_hours: 'high',
  needs_attendance: 'high',
  submitted: 'normal',
  reviewed: 'normal',
  approved: 'low',
  client_approved: 'low',
}

/**
 * Group notifications by date and type for cleaner display
 */
const groupNotifications = (notifications) => {
  const groups = {}
  if (!Array.isArray(notifications)) return groups;
  
  const now = new Date()
  const today = now.toDateString()
  const yesterday = new Date(now - 86400000).toDateString()

  for (const n of notifications) {
    const date = parseUTC(n.created_at)
    let label
    if (date.toDateString() === today) label = 'Today'
    else if (date.toDateString() === yesterday) label = 'Yesterday'
    else label = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })

    if (!groups[label]) groups[label] = []
    groups[label].push(n)
  }

  return groups
}

export const NotificationCenterProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [panelOpen, setPanelOpen] = useState(false)
  const sseRef = useRef(null)
  const pollRef = useRef(null)
  const mountedRef = useRef(true)

  // Check if user is authenticated — use employee_id first (matches super_foreman_assignments),
  // fall back to identity service id for users without an HR employee record
  const getUserId = () => {
    try {
      const user = JSON.parse(localStorage.getItem(`${APP_STORAGE_PREFIX}_user`) || '{}')
      return user.employee_id || user.id
    } catch {
      return null
    }
  }

  // Fetch notifications from backend
  const fetchNotifications = useCallback(async (opts = {}) => {
    const userId = getUserId()
    const token = getAccessToken()
    if (!userId || !token) return

    if (!opts.silent) setLoading(true)
    try {
      const [notifRes, countRes] = await Promise.all([
        notificationsAPI.getAll({ limit: 50 }),
        notificationsAPI.getUnreadCount(),
      ])
      if (mountedRef.current) {
        const data = notifRes.data;
        const items = Array.isArray(data) ? data : (data?.notifications || data?.items || []);
        setNotifications(items)
        setUnreadCount(countRes.data?.unread_count || 0)
      }
    } catch (err) {
      console.error('Failed to fetch notifications:', err)
    } finally {
      if (mountedRef.current) setLoading(false)
    }
  }, [])

  // Mark specific notifications as read
  const markAsRead = useCallback(async (notificationIds) => {
    if (!notificationIds?.length) return
    try {
      await notificationsAPI.markAsRead(notificationIds)
      setNotifications(prev =>
        prev.map(n => notificationIds.includes(n.id) ? { ...n, is_read: true } : n)
      )
      setUnreadCount(prev => Math.max(0, prev - notificationIds.length))
    } catch (err) {
      console.error('Failed to mark notifications as read:', err)
    }
  }, [])

  /**
   * A guided-tour practice run has ended, so ask the server what is actually unread.
   *
   * `markAsRead` decrements the badge locally on a 2xx, and during a practice run that 2xx is
   * synthesised — so opening the panel on a practice run really does drop the bell from 2 to 1 with
   * nothing on the wire, and the notification the reader "read" is still waiting for them.
   *
   * This provider sits ABOVE TourProvider in App.jsx, which is exactly why the reset is a plain
   * module subscription and not something handed down through the tour's React context: a surface
   * that a practice run can lie to is not always a surface the tour can reach.
   */
  usePracticeReset(() => { fetchNotifications({ silent: true }) })

  // Mark all as read
  const markAllAsRead = useCallback(async () => {
    const unreadIds = notifications.filter(n => !n.is_read).map(n => n.id)
    if (unreadIds.length) await markAsRead(unreadIds)
  }, [notifications, markAsRead])

  // Toggle notification panel
  const togglePanel = useCallback(() => {
    setPanelOpen(prev => !prev)
  }, [])

  const closePanel = useCallback(() => {
    setPanelOpen(false)
  }, [])

  // Connect SSE for real-time updates
  const connectSSE = useCallback(() => {
    if (sseRef.current) {
      sseRef.current.close()
      sseRef.current = null
    }

    const userId = getUserId()
    const token = getAccessToken()
    if (!userId || !token) return

    const source = notificationsAPI.connectSSE(
      (data) => {
        // New notification arrived via SSE
        if (data.type === 'notification') {
          setNotifications(prev => [data.notification, ...prev])
          setUnreadCount(prev => prev + 1)
        } else if (data.type === 'count_update') {
          setUnreadCount(data.unread_count)
        }
      },
      () => {
        // SSE failed — fall back to polling
        console.warn('SSE connection lost, falling back to polling')
        startPolling()
      }
    )

    if (source) {
      sseRef.current = source
      // Stop polling if SSE connects
      if (pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
    }
  }, [])

  // Fallback polling every 30 seconds
  const startPolling = useCallback(() => {
    if (pollRef.current) return
    pollRef.current = setInterval(() => {
      fetchNotifications({ silent: true })
    }, 30000)
  }, [fetchNotifications])

  // Initialize on mount
  useEffect(() => {
    mountedRef.current = true
    const userId = getUserId()
    if (!userId) return

    // Initial fetch
    fetchNotifications()

    // Try SSE first, fall back to polling
    connectSSE()

    // If SSE didn't connect, start polling
    if (!sseRef.current) {
      startPolling()
    }

    return () => {
      mountedRef.current = false
      if (sseRef.current) {
        sseRef.current.close()
        sseRef.current = null
      }
      if (pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
    }
  }, [fetchNotifications, connectSSE, startPolling])

  // Re-initialize when user changes (login/logout)
  useEffect(() => {
    const handleStorage = (e) => {
      // Phase B3: the access-token key no longer exists in storage and can never fire.
      if (e.key === `${APP_STORAGE_PREFIX}_user`) {
        const userId = getUserId()
        if (userId) {
          fetchNotifications()
          connectSSE()
        } else {
          setNotifications([])
          setUnreadCount(0)
          if (sseRef.current) sseRef.current.close()
          if (pollRef.current) clearInterval(pollRef.current)
        }
      }
    }
    window.addEventListener('storage', handleStorage)
    return () => window.removeEventListener('storage', handleStorage)
  }, [fetchNotifications, connectSSE])

  const grouped = groupNotifications(notifications)

  const value = {
    notifications,
    grouped,
    unreadCount,
    loading,
    panelOpen,
    togglePanel,
    closePanel,
    markAsRead,
    markAllAsRead,
    refresh: fetchNotifications,
    getPriority: (type) => PRIORITY_MAP[type] || 'normal',
  }

  return (
    <NotificationCenterContext.Provider value={value}>
      {children}
    </NotificationCenterContext.Provider>
  )
}

export default NotificationCenterProvider
